import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Scroll, BookOpen, ChevronRight, Sparkles } from 'lucide-react';
import { prophetApi } from '../services/api';
import { 
  SectionHeader, 
  PageLoading, 
  GlassCard,
  CertaintyBadge 
} from '../components/shared';

const Prophets = () => {
  const { t } = useTranslation();
  const [prophets, setProphets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProphets = async () => {
      try {
        const response = await prophetApi.getProphets();
        // Remove the generic "Muhammad/Mohammed" entry from the database
        // list — the dedicated Sīra timeline card above replaces it so we
        // don't show two Muhammad ﷺ cards.
        const filtered = (response.data || []).filter((p) => {
          const en = (p.name_english || '').toLowerCase();
          const fr = (p.name_french || '').toLowerCase();
          return !['muhammad', 'mohammed', 'muhammed', 'mohamed', 'mohammad']
            .some((alias) => en.includes(alias) || fr.includes(alias));
        });
        setProphets(filtered);
      } catch (error) {
        console.error('Error fetching prophets:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProphets();
  }, []);

  if (loading) return <PageLoading />;

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <div 
        className="relative py-20 px-4 bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1683138155815-d7edd806d8a3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwyfHxkZXNlcnQlMjBuaWdodCUyMHNreSUyMHN0YXJzfGVufDB8fHx8MTc3NTQ2OTA2MXww&ixlib=rb-4.1.0&q=85)'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-background/90 via-background/80 to-background" />
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <span className="overline mb-4 block">{t('prophets.overline')}</span>
          <h1 className="font-arabic text-4xl sm:text-5xl text-primary mb-4" dir="rtl">
            الأنبياء
          </h1>
          <h2 className="text-3xl sm:text-4xl font-serif text-text-primary mb-4">
            {t('prophets.title')}
          </h2>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            {t('prophets.subtitle')}
          </p>
        </div>
      </div>

      {/* Timeline */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="relative">
          {/* Central line */}
          <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-transparent via-primary to-transparent" />

          <div className="space-y-8">
            {/* Special card: Prophet Muhammad ﷺ — keeps the dedicated
                Sīra timeline page (/prophet-timeline) untouched but makes
                it discoverable from the Prophets list. */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative pl-20"
              data-testid="prophet-muhammad-special"
            >
              <div className="absolute left-6 top-6 w-5 h-5 rounded-full bg-amber-300 border-4 border-background z-10" />
              <div className="absolute left-0 top-5 w-12 h-6 flex items-center justify-center">
                <Sparkles className="w-3 h-3 text-amber-300" />
              </div>
              <Link to="/prophet-timeline" data-testid="prophet-muhammad-link">
                <GlassCard className="hover:border-amber-400/40 transition-all cursor-pointer group border-amber-400/20">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-500/15 text-amber-300 border border-amber-500/30">
                          Sīra détaillée · Timeline
                        </span>
                      </div>
                      <h3 className="font-serif text-xl text-text-primary group-hover:text-amber-300 transition-colors">
                        Le Prophète Muhammad ﷺ
                      </h3>
                      <p className="font-arabic text-2xl text-amber-300 mt-1" dir="rtl">
                        مُحَمَّد ﷺ
                      </p>
                      <p className="text-sm text-text-muted">Muhammad — sceau des prophètes</p>
                      <p className="text-sm text-text-secondary mt-3 line-clamp-2">
                        Page dédiée avec une timeline complète de la Sīra (vie du Prophète),
                        des événements marquants et leur contexte historique.
                      </p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-text-muted group-hover:text-amber-300 transition-colors flex-shrink-0" />
                  </div>
                </GlassCard>
              </Link>
            </motion.div>

            {prophets.map((prophet, index) => (
              <motion.div
                key={prophet.id}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="relative pl-20"
                data-testid={`prophet-${prophet.id}`}
              >
                {/* Node */}
                <div className="absolute left-6 top-6 w-5 h-5 rounded-full bg-primary border-4 border-background z-10" />
                
                {/* Order number */}
                <div className="absolute left-0 top-5 w-12 h-6 flex items-center justify-center">
                  <span className="text-xs text-primary font-medium">{prophet.order}</span>
                </div>

                <Link to={`/prophets/${prophet.id}`}>
                  <GlassCard className="hover:border-primary/30 transition-all cursor-pointer group">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <CertaintyBadge level={prophet.certainty_level} />
                          {prophet.era && (
                            <span className="text-xs text-text-muted">{prophet.era}</span>
                          )}
                        </div>
                        
                        <h3 className="font-serif text-xl text-text-primary group-hover:text-primary transition-colors">
                          {prophet.name_french || prophet.name_english}
                        </h3>
                        <p className="font-arabic text-2xl text-primary mt-1" dir="rtl">
                          {prophet.name_arabic}
                        </p>
                        <p className="text-sm text-text-muted">{prophet.name_english}</p>
                        
                        <p className="text-sm text-text-secondary mt-3 line-clamp-2">
                          {prophet.summary}
                        </p>
                        
                        {prophet.quran_mentions?.length > 0 && (
                          <div className="flex items-center gap-2 mt-4 text-xs text-text-muted">
                            <BookOpen className="w-4 h-4" />
                            <span>{t('prophets.mentioned_in_verses', { count: prophet.quran_mentions.length })}</span>
                          </div>
                        )}
                        
                        {prophet.location && (
                          <p className="text-xs text-text-muted mt-2">📍 {prophet.location}</p>
                        )}
                      </div>
                      
                      <ChevronRight className="w-5 h-5 text-text-muted group-hover:text-primary transition-colors flex-shrink-0" />
                    </div>
                  </GlassCard>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>

        {prophets.length === 0 && (
          <div className="text-center py-16">
            <Scroll className="w-16 h-16 text-text-muted mx-auto mb-4" />
            <h3 className="text-lg font-medium text-text-primary mb-2">{t('prophets.no_prophets')}</h3>
            <p className="text-text-secondary">{t('prophets.loading_data')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Prophets;
